import { useState, useEffect } from 'react';
import { collection, doc, getDocs, limit, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';

export interface WalletData {
  free_usage: number;
  free_usage_limit: number;
  token_usage: number;
  available_tokens: number;
  amount: number;
  updated_at: Date;
}

export const useWallet = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [walletData, setWalletData] = useState<WalletData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchWalletData = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      // Wallets are created by the backend with an auto-generated document
      // ID, so look them up by their user reference instead of assuming the
      // document ID is the Firebase user ID.
      const walletQuery = query(
        collection(db, 'wallet'),
        where('user_ref', '==', doc(db, 'users', user.uid)),
        limit(1),
      );
      const walletSnapshot = await getDocs(walletQuery);

      if (!walletSnapshot.empty) {
        const data = walletSnapshot.docs[0].data();
        setWalletData({
          free_usage: data.free_usage,
          free_usage_limit: data.free_usage_limit,
          token_usage: data.token_usage,
          available_tokens: data.available_tokens,
          amount: data.amount,
          updated_at: data.updated_at?.toDate()
        });
      }
    } catch (err) {
      console.error('Error fetching wallet data:', err);
      setError('Failed to load wallet data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWalletData();
  }, [user]);

  return { walletData, loading, error, refresh: fetchWalletData };
};
